import { UserContext } from './UserContext';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  fetchUserService,
  logoutUserService,
  switchRoleService,
} from '../../global-services/user.service';
import { logAnalyticsEvent } from '../../config/firebase';
import {
  formatCurrency,
  getExchangeRates,
  convertFromCAD,
  convertToCAD,
} from '../../utils/currency';

function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [rates, setRates] = useState(null);

  const loggedIn = !!user;
  const navigate = useNavigate();

  useEffect(() => {
    const initUser = async () => {
      setLoading(true);
      try {
        const [userData, exchangeRates] = await Promise.all([
          fetchUserService(),
          getExchangeRates(),
        ]);
        setUser(userData);
        setRates(exchangeRates);
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initUser();
  }, []);

  /**
   * Global currency converter and formatter.
   * Converts amount from CAD to user's preferred currency and formats it.
   */
  const convertAndFormat = (amount) => {
    if (amount === undefined || amount === null)
      return formatCurrency(0, user?.currency || 'CAD');
    const converted = convertFromCAD(amount, user?.currency || 'CAD', rates);
    return formatCurrency(converted, user?.currency || 'CAD');
  };

  /**
   * Converts an amount from user's preferred currency to CAD.
   */
  const convertToCADPref = (amount) => {
    if (!amount) return 0;
    return convertToCAD(amount, user?.currency || 'CAD', rates);
  };

  /**
   * Returns symbol of the user's preferred currency.
   */
  const getCurrencySymbol = () => {
    try {
      return (0)
        .toLocaleString('en-CA', {
          style: 'currency',
          currency: user?.currency || 'CAD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim();
    } catch (e) {
      return '$';
    }
  };

  /* LOGOUT */
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logoutUserService();
      logAnalyticsEvent('logout');
      setUser(null);

      // Clear session flags
      sessionStorage.removeItem('profileNoticeShown');
      sessionStorage.removeItem('dismissedGovIdModal');

      // Navigate to home and EXPLICITLY clear state to prevent auto-login trigger
      navigate('/', { replace: true, state: {} });
      toast.success('Logged Out Successfully');
    } catch {
      toast.error('Logout failed. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  /* SWITCH ROLE */
  const switchRole = async (role) => {
    try {
      const active_role = await switchRoleService(role);
      setUser({ ...user, active_role });
      toast.success(`Switched role to ${role}`);
    } catch (err) {
      toast.error('Failed to switch role');
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        loggedIn,
        loading,
        handleLogout,
        switchRole,
        isLoggingOut,
        showPendingModal,
        setShowPendingModal,
        formatCurrency,
        convertAndFormat,
        convertToCAD: convertToCADPref,
        getCurrencySymbol,
        rates,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default UserProvider;
